import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { IconButtonAnimate } from "../../../components/animate";
import { PATH_DASHBOARD } from "../../../routes/path";
import {
    Badge,
    Box,
    Button,
    Chip,
    Divider,
    IconButton,
    List,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    ListSubheader,
    Stack,
    Tooltip,
    Typography
} from "@mui/material";
import Iconify from "../../../components/Iconify";
import MenuPopover from "../../../components/MenuPopover";
import Scrollbar from "../../../components/Scrollbar";
import { useSelector } from "react-redux";
import { dispatch } from "../../../redux/store";
import {
    dismissExpiryNotification,
    fetchExpiryNotifications,
    fetchNotificationPreferences,
    markAllExpiryNotificationsAsRead,
    markExpiryNotificationAsRead
} from "../../../redux/slices/expiryNotifications";

const SEVERITY_CONFIG = {
    CRITICAL: { color: '#f44336', bgColor: '#ffebee', icon: 'eva:alert-triangle-fill', label: 'Kritično' },
    WARNING: { color: '#ff9800', bgColor: '#fff3e0', icon: 'eva:alert-circle-fill', label: 'Upozorenje' },
    INFO: { color: '#2196f3', bgColor: '#e3f2fd', icon: 'eva:info-fill', label: 'Info' },
};

const ENTITY_TYPE_LABELS = {
    RATE_CARD: 'Cenovnik',
    LINE: 'Linija',
    TRIP: 'Polazak',
    ALLOTMENT: 'Alotman',
};

export default function ExpiryNotificationsPopover() {
    const { notifications, unreadCount, notificationsEnabled } = useSelector((state) => state.expiryNotifications);
    const [openPopover, setOpenPopover] = useState(null);
    const navigate = useNavigate();

    useEffect(() => {
        dispatch(fetchNotificationPreferences());
    }, []);

    useEffect(() => {
        if (notificationsEnabled) {
            dispatch(fetchExpiryNotifications());
        }
    }, [notificationsEnabled]);

    const handleOpenPopover = (event) => {
        setOpenPopover(event.currentTarget);
    };

    const handleClosePopover = () => {
        setOpenPopover(null);
    };

    const handleMarkAllAsRead = () => {
        dispatch(markAllExpiryNotificationsAsRead());
    };

    const handleMarkAsRead = (id) => {
        dispatch(markExpiryNotificationAsRead(id));
    };

    const handleDismiss = (e, id) => {
        e.stopPropagation();
        dispatch(dismissExpiryNotification(id));
    };

    const open = Boolean(openPopover);

    // if (!notificationsEnabled) return null;

    const criticalNotifications = notifications.filter(n => n.severity === 'CRITICAL');
    const warningNotifications = notifications.filter(n => n.severity === 'WARNING');
    const infoNotifications = notifications.filter(n => n.severity === 'INFO');

    return (
        <>
            <IconButtonAnimate
                color={openPopover ? 'primary' : 'default'}
                onClick={handleOpenPopover}
                sx={{ width: 40, height: 40 }}
            >
                <Badge badgeContent={unreadCount} color="error">
                    <Iconify icon="eva:bell-fill" />
                </Badge>
            </IconButtonAnimate>

            <MenuPopover openPopover={openPopover} open={open} onClose={handleClosePopover}
                         sx={{ width: 400, p: 0, maxHeight: 500 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', py: 2, px: 2.5 }}>
                    <Box sx={{ flexGrow: 1 }}>
                        <Typography variant="subtitle1">Obaveštenja</Typography>
                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                            {unreadCount > 0
                                ? `Imate ${unreadCount} nepročitanih obaveštenja`
                                : 'Nema novih obaveštenja'}
                        </Typography>
                    </Box>

                    {unreadCount > 0 && (
                        <Tooltip title="Označi sve kao pročitano">
                            <IconButton color="primary" onClick={handleMarkAllAsRead}>
                                <Iconify icon="eva:done-all-fill" />
                            </IconButton>
                        </Tooltip>
                    )}
                </Box>

                <Divider sx={{ borderStyle: 'dashed' }} />

                <Scrollbar sx={{ maxHeight: 340 }}>
                    {notifications.length === 0 && (
                        <Box sx={{ py: 4, textAlign: 'center' }}>
                            <Iconify icon="eva:checkmark-circle-2-fill" sx={{ width: 48, height: 48, color: 'success.main', mb: 1 }} />
                            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                Svi elementi su važeći
                            </Typography>
                        </Box>
                    )}

                    <NotificationGroup
                        title="Kritično (≤7 dana)"
                        notifications={criticalNotifications}
                        onRead={handleMarkAsRead}
                        onDismiss={handleDismiss}
                    />
                    <NotificationGroup
                        title="Upozorenje (8-15 dana)"
                        notifications={warningNotifications}
                        onRead={handleMarkAsRead}
                        onDismiss={handleDismiss}
                    />
                    <NotificationGroup
                        title="Informacija (16-30 dana)"
                        notifications={infoNotifications}
                        onRead={handleMarkAsRead}
                        onDismiss={handleDismiss}
                    />
                </Scrollbar>

                <Divider sx={{ borderStyle: 'dashed' }} />

                <Box sx={{ p: 1 }}>
                    <Button fullWidth disableRipple onClick={() => {
                        handleClosePopover();
                        navigate(PATH_DASHBOARD.general.notifications);
                    }}>
                        Pogledaj sva obaveštenja
                    </Button>
                </Box>
            </MenuPopover>
        </>
    );
}

function NotificationGroup({ title, notifications, onRead, onDismiss }) {
    if (!notifications || notifications.length === 0) return null;

    return (
        <List
            disablePadding
            subheader={
                <ListSubheader disableSticky sx={{ py: 1, px: 2.5, typography: 'overline' }}>
                    {title}
                </ListSubheader>
            }
        >
            {notifications.map((notification) => (
                <ExpiryNotificationItem
                    key={notification.id}
                    notification={notification}
                    onRead={onRead}
                    onDismiss={onDismiss}
                />
            ))}
        </List>
    );
}

function ExpiryNotificationItem({ notification, onRead, onDismiss }) {
    const severityConfig = SEVERITY_CONFIG[notification.severity] || SEVERITY_CONFIG.INFO;
    const entityLabel = ENTITY_TYPE_LABELS[notification.entityType] || notification.entityType;

    return (
        <ListItemButton
            onClick={() => onRead(notification.id)}
            sx={{
                py: 1.5,
                px: 2.5,
                mt: '1px',
                ...(!notification.isRead && {
                    bgcolor: severityConfig.bgColor,
                }),
            }}
        >
            <ListItemIcon sx={{ minWidth: 36 }}>
                <Iconify
                    icon={severityConfig.icon}
                    sx={{ width: 24, height: 24, color: severityConfig.color }}
                />
            </ListItemIcon>

            <ListItemText
                disableTypography
                primary={
                    <Stack direction="row" alignItems="center" spacing={1}>
                        <Typography variant="subtitle2" sx={{ flexGrow: 1 }}>
                            {notification.entityName || entityLabel}
                        </Typography>
                        <Chip
                            label={`${notification.daysRemaining}d`}
                            size="small"
                            sx={{
                                height: 20,
                                fontSize: '0.7rem',
                                fontWeight: 700,
                                bgcolor: severityConfig.color,
                                color: 'white',
                            }}
                        />
                    </Stack>
                }
                secondary={
                    <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mt: 0.5 }}>
                        <Typography variant="caption" sx={{ color: 'text.disabled' }}>
                            {entityLabel} • Ističe {notification.expiryDate}
                        </Typography>
                        <Tooltip title="Odbaci">
                            <IconButton size="small" onClick={(e) => onDismiss(e, notification.id)}>
                                <Iconify icon="eva:close-fill" sx={{ width: 16, height: 16 }} />
                            </IconButton>
                        </Tooltip>
                    </Stack>
                }
            />
        </ListItemButton>
    );
}
